import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  getCountFromServer,
  getAggregateFromServer,
  sum as fsSum,
  average as fsAverage,
  count as fsCount,
  onSnapshot,
  runTransaction,
  writeBatch,
  query,
  where,
  orderBy as fsOrderBy,
  limit as fsLimit,
  limitToLast,
  startAfter,
  endBefore,
  documentId,
  AggregateField,
  CollectionReference,
  QueryConstraint,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { BusinessIdResolver } from './business-id-resolver';
import { encodeForWrite, encodeQueryValue, fromFirestore } from './firestore-codec';
import {
  AggregateSpec,
  AppendOnlyCollection,
  CollectionPath,
  DocBase,
  NewDoc,
  Page,
  QuerySpec,
  RootDb,
  SetOptions,
  TenantBatch,
  TenantCollection,
  TenantDb,
  TenantTxn,
  orderKeys,
  pageCursors,
} from './tenant-collection';

function segments(path: CollectionPath): string[] {
  return Array.isArray(path) ? path : [path];
}

/** Build a Firestore collection ref from full path segments inside an injection context. */
function collectionFromSegments(firestore: Firestore, injector: Injector, segs: string[]) {
  return runInInjectionContext(injector, () => collection(firestore, segs[0], ...segs.slice(1)));
}

/**
 * Firestore adapter for a collection. Owns injection context, the codec, and query
 * translation — so services never touch Firestore. Where the collection lives (tenant-scoped
 * under businesses/{id} or top-level) is decided by `resolveSegments`, supplied by the parent db.
 */
class FirestoreCollection<T extends DocBase> implements TenantCollection<T> {
  constructor(
    private readonly firestore: Firestore,
    private readonly injector: Injector,
    /** Resolves the full path segments to this collection (e.g. ['businesses', bid, 'clients']). */
    private readonly resolveSegments: () => Promise<string[]>
  ) {}

  private async ref(): Promise<CollectionReference> {
    const segs = await this.resolveSegments();
    return collectionFromSegments(this.firestore, this.injector, segs);
  }

  private constraints(spec?: QuerySpec): QueryConstraint[] {
    const out: QueryConstraint[] = [];
    for (const [field, op, value] of spec?.where ?? []) {
      out.push(where(field, op, encodeQueryValue(value)));
    }
    for (const { field, direction } of orderKeys(spec)) {
      out.push(fsOrderBy(field, direction ?? 'asc'));
    }
    if (spec?.after) out.push(startAfter(encodeQueryValue(spec.after.value)));
    if (spec?.before) out.push(endBefore(encodeQueryValue(spec.before.value)));
    if (spec?.limit != null) {
      // Backward pagination keeps the `limit` rows *preceding* the cursor.
      out.push(spec.before ? limitToLast(spec.limit) : fsLimit(spec.limit));
    }
    return out;
  }

  async list(spec?: QuerySpec): Promise<T[]> {
    const ref = await this.ref();
    return runInInjectionContext(this.injector, async () => {
      const snap = await getDocs(query(ref, ...this.constraints(spec)));
      return snap.docs.map(d => fromFirestore<T>(d.data() as Record<string, unknown>, d.id));
    });
  }

  async page(spec: QuerySpec): Promise<Page<T>> {
    const items = await this.list(spec);
    return { items, ...pageCursors(items, spec) };
  }

  async get(id: string): Promise<T | undefined> {
    const ref = await this.ref();
    return runInInjectionContext(this.injector, async () => {
      const snap = await getDoc(doc(ref, id));
      return snap.exists()
        ? fromFirestore<T>(snap.data() as Record<string, unknown>, snap.id)
        : undefined;
    });
  }

  async getMany(ids: string[]): Promise<T[]> {
    if (ids.length === 0) return [];
    const ref = await this.ref();
    return runInInjectionContext(this.injector, async () => {
      // Firestore documentId() 'in' supports max 30 values per query.
      const chunks: string[][] = [];
      for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));
      const snaps = await Promise.all(
        chunks.map(chunk => getDocs(query(ref, where(documentId(), 'in', chunk))))
      );
      return snaps.flatMap(s =>
        s.docs.map(d => fromFirestore<T>(d.data() as Record<string, unknown>, d.id))
      );
    });
  }

  async count(spec?: QuerySpec): Promise<number> {
    const ref = await this.ref();
    return runInInjectionContext(this.injector, async () => {
      const snap = await getCountFromServer(query(ref, ...this.constraints(spec)));
      return snap.data().count;
    });
  }

  async aggregate(
    spec: QuerySpec | undefined,
    fields: AggregateSpec
  ): Promise<Record<string, number>> {
    const ref = await this.ref();
    return runInInjectionContext(this.injector, async () => {
      const aggs: Record<string, AggregateField<number>> = {};
      for (const [key, f] of Object.entries(fields)) {
        aggs[key] =
          f.op === 'count' ? fsCount() : f.op === 'sum' ? fsSum(f.field) : fsAverage(f.field);
      }
      const snap = await getAggregateFromServer(query(ref, ...this.constraints(spec)), aggs);
      // average() returns number | null when no docs match; normalize null -> 0.
      const data = snap.data() as Record<string, number | null>;
      const out: Record<string, number> = {};
      for (const key of Object.keys(fields)) out[key] = data[key] ?? 0;
      return out;
    });
  }

  async add(data: NewDoc<T>): Promise<string> {
    const ref = await this.ref();
    return runInInjectionContext(this.injector, async () => {
      const docRef = await addDoc(ref, encodeForWrite(data as Record<string, unknown>, 'create'));
      return docRef.id;
    });
  }

  async set(id: string, data: NewDoc<T>, opts?: SetOptions): Promise<void> {
    const ref = await this.ref();
    const encoded = encodeForWrite(
      data as Record<string, unknown>,
      opts?.merge ? 'update' : 'create'
    );
    return runInInjectionContext(this.injector, () =>
      opts?.merge ? setDoc(doc(ref, id), encoded, { merge: true }) : setDoc(doc(ref, id), encoded)
    );
  }

  async update(id: string, patch: Partial<NewDoc<T>>): Promise<void> {
    const ref = await this.ref();
    return runInInjectionContext(this.injector, () =>
      updateDoc(doc(ref, id), encodeForWrite(patch as Record<string, unknown>, 'update'))
    );
  }

  async delete(id: string): Promise<void> {
    const ref = await this.ref();
    return runInInjectionContext(this.injector, () => deleteDoc(doc(ref, id)));
  }

  observe(spec?: QuerySpec): Observable<T[]> {
    return new Observable<T[]>(subscriber => {
      let unsub = () => {};
      this.ref()
        .then(ref =>
          runInInjectionContext(this.injector, () => {
            unsub = onSnapshot(
              query(ref, ...this.constraints(spec)),
              snap =>
                subscriber.next(
                  snap.docs.map(d => fromFirestore<T>(d.data() as Record<string, unknown>, d.id))
                ),
              err => subscriber.error(err)
            );
          })
        )
        .catch(err => subscriber.error(err));
      return () => unsub();
    });
  }

  observeDoc(id: string): Observable<T | undefined> {
    return new Observable<T | undefined>(subscriber => {
      let unsub = () => {};
      this.ref()
        .then(ref =>
          runInInjectionContext(this.injector, () => {
            unsub = onSnapshot(
              doc(ref, id),
              snap =>
                subscriber.next(
                  snap.exists()
                    ? fromFirestore<T>(snap.data() as Record<string, unknown>, snap.id)
                    : undefined
                ),
              err => subscriber.error(err)
            );
          })
        )
        .catch(err => subscriber.error(err));
      return () => unsub();
    });
  }
}

/**
 * Shared Firestore adapter logic for both the tenant-scoped and root (top-level) seams.
 * Subclasses supply `prefix()` — the path segments prepended before a collection path
 * (`['businesses', businessId]` for the tenant seam, `[]` for the root seam).
 */
abstract class FirestoreDbBase {
  protected abstract readonly firestore: Firestore;
  protected abstract readonly injector: Injector;
  /** Segments prepended before every collection path. */
  protected abstract prefix(): Promise<string[]>;

  private ic<T>(f: () => T): T {
    return runInInjectionContext(this.injector, f);
  }

  collection<T extends DocBase>(path: CollectionPath): TenantCollection<T> {
    return new FirestoreCollection<T>(this.firestore, this.injector, async () => [
      ...(await this.prefix()),
      ...segments(path),
    ]);
  }

  /** Append-only is the same reads/add with the mutating methods hidden by the type. */
  appendOnly<T extends DocBase>(path: CollectionPath): AppendOnlyCollection<T> {
    return this.collection<T>(path);
  }

  async runTransaction<R>(fn: (txn: TenantTxn) => Promise<R>): Promise<R> {
    const prefix = await this.prefix();
    // Raw ref builders (no injection context); every Firebase call below is wrapped in `ic`
    // because runTransaction invokes its callback asynchronously, outside the outer context.
    const rawDoc = (path: CollectionPath, id: string) => {
      const s = [...prefix, ...segments(path), id];
      return doc(this.firestore, s[0], ...s.slice(1));
    };
    const rawCol = (path: CollectionPath) => {
      const s = [...prefix, ...segments(path)];
      return collection(this.firestore, s[0], ...s.slice(1));
    };
    return this.ic(() =>
      runTransaction(this.firestore, async tx => {
        const txn: TenantTxn = {
          get: (path, id) =>
            this.ic(async () => {
              const snap = await tx.get(rawDoc(path, id));
              return snap.exists()
                ? fromFirestore(snap.data() as Record<string, unknown>, snap.id)
                : undefined;
            }),
          add: (path, data) =>
            this.ic(() => {
              const ref = doc(rawCol(path));
              tx.set(ref, encodeForWrite(data as Record<string, unknown>, 'create'));
              return ref.id;
            }),
          set: (path, id, data, opts) =>
            this.ic(() => {
              const ref = rawDoc(path, id);
              const enc = encodeForWrite(
                data as Record<string, unknown>,
                opts?.merge ? 'update' : 'create'
              );
              return opts?.merge ? tx.set(ref, enc, { merge: true }) : tx.set(ref, enc);
            }) as unknown as void,
          update: (path, id, patch) =>
            this.ic(() =>
              tx.update(
                rawDoc(path, id),
                encodeForWrite(patch as Record<string, unknown>, 'update')
              )
            ) as unknown as void,
          delete: (path, id) => this.ic(() => tx.delete(rawDoc(path, id))) as unknown as void,
        };
        return fn(txn);
      })
    );
  }

  async runBatch(fn: (batch: TenantBatch) => void | Promise<void>): Promise<void> {
    const prefix = await this.prefix();
    const rawDoc = (path: CollectionPath, id: string) => {
      const s = [...prefix, ...segments(path), id];
      return doc(this.firestore, s[0], ...s.slice(1));
    };
    const rawCol = (path: CollectionPath) => {
      const s = [...prefix, ...segments(path)];
      return collection(this.firestore, s[0], ...s.slice(1));
    };
    const batch = this.ic(() => writeBatch(this.firestore));
    const writer: TenantBatch = {
      add: (path, data) =>
        this.ic(() => {
          const ref = doc(rawCol(path));
          batch.set(ref, encodeForWrite(data as Record<string, unknown>, 'create'));
          return ref.id;
        }),
      set: (path, id, data, opts) =>
        this.ic(() => {
          const ref = rawDoc(path, id);
          const enc = encodeForWrite(
            data as Record<string, unknown>,
            opts?.merge ? 'update' : 'create'
          );
          return opts?.merge ? batch.set(ref, enc, { merge: true }) : batch.set(ref, enc);
        }) as unknown as void,
      update: (path, id, patch) =>
        this.ic(() =>
          batch.update(rawDoc(path, id), encodeForWrite(patch as Record<string, unknown>, 'update'))
        ) as unknown as void,
      delete: (path, id) => this.ic(() => batch.delete(rawDoc(path, id))) as unknown as void,
    };
    await fn(writer);
    await this.ic(() => batch.commit());
  }
}

/** Tenant-scoped adapter: every path lives under `businesses/{currentBusinessId}/…`. */
@Injectable({ providedIn: 'root' })
export class FirestoreTenantDb extends FirestoreDbBase implements TenantDb {
  protected firestore = inject(Firestore);
  protected injector = inject(Injector);
  private resolver = inject(BusinessIdResolver);

  protected async prefix(): Promise<string[]> {
    return ['businesses', await this.resolver.require()];
  }
}

/**
 * Root adapter: paths are top-level (e.g. `subscriptions`, `invites`, `plan_catalog`), with no
 * business scoping. Callers add `where('businessId', '==', id)` themselves for cross-tenant
 * collections that store a businessId field. Same codec, query DSL, transactions, and fake.
 */
@Injectable({ providedIn: 'root' })
export class FirestoreRootDb extends FirestoreDbBase implements RootDb {
  protected firestore = inject(Firestore);
  protected injector = inject(Injector);

  protected async prefix(): Promise<string[]> {
    return [];
  }
}
